import React, { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';

interface CameraScannerProps {
  onScanComplete: (qrUrl: string, serialNumber: string) => void;
  onClose: () => void;
}

export const CameraScanner: React.FC<CameraScannerProps> = ({ onScanComplete, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const [detectedQrUrl, setDetectedQrUrl] = useState<string>('');
  const [detectedSerial, setDetectedSerial] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [inversionEnabled, setInversionEnabled] = useState(false);

  // Initialize QR scanner
  useEffect(() => {
    if (!videoRef.current) return;

    const scanner = new QrScanner(
      videoRef.current,
      (result: QrScanner.ScanResult) => {
        console.log('QR Scanner callback triggered');
        if (result.data) {
          console.log('QR data detected:', result.data);
          setDetectedQrUrl(result.data);
        } else {
          console.log('QR callback triggered but no data');
        }
      },
      {
        returnDetailedScanResult: true,
        highlightScanRegion: true,
        highlightCodeOutline: true,
      }
    );

    scannerRef.current = scanner;

    scanner.start().catch(() => {
      setError('Failed to access camera. Please ensure camera permissions are granted.');
    });

    return () => {
      scanner.stop();
      scanner.destroy();
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    if (detectedQrUrl && detectedSerial) {
      onScanComplete(detectedQrUrl, detectedSerial);
    }
  };

  const handleInversionToggle = () => {
    const newEnabled = !inversionEnabled;
    setInversionEnabled(newEnabled);
    if (scannerRef.current) {
      scannerRef.current.setInversionMode(newEnabled ? 'both' : 'original');
    }
  };




  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>📷 Scan QR Code</h2>
          <button onClick={onClose} style={closeButtonStyle}>✕</button>
        </div>

        <div style={contentStyle}>
          <div style={videoContainerStyle}>
            <video ref={videoRef} style={videoStyle} />
            <canvas ref={canvasRef} style={{ display: 'none' }} />



            {/* Status indicators */}
            <div style={statusOverlayStyle}>
              <div style={statusItemStyle}>
                <span style={{ ...statusDotStyle, backgroundColor: detectedQrUrl ? '#4CAF50' : '#FFC107' }} />
                QR Code: {detectedQrUrl ? '✓ Detected' : 'Scanning...'}
              </div>
              <div style={statusItemStyle}>
                <span style={{ ...statusDotStyle, backgroundColor: detectedSerial ? '#4CAF50' : '#FFC107' }} />
                Serial: {detectedSerial || 'Enter manually below'}
              </div>
            </div>

            {/* Inversion toggle */}
            <div style={controlsOverlayStyle}>
              <button
                onClick={handleInversionToggle}
                style={inversionEnabled ? inversionButtonActiveStyle : inversionButtonStyle}
                title={inversionEnabled
                  ? 'Scanning both normal and inverted QR codes'
                  : 'Click to enable inverted QR code scanning'}
              >
                Invert: {inversionEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          {error && (
            <div style={errorStyle}>
              ❌ {error}
            </div>
          )}

          {/* Detection results */}
          <div style={resultsStyle}>
            <div style={resultItemStyle}>
              <strong>QR URL:</strong>
              <span style={resultValueStyle}>
                {detectedQrUrl || 'Not detected yet'}
              </span>
            </div>
            <div style={resultItemStyle}>
              <strong>Serial Number:</strong>
              <input
                type="text"
                value={detectedSerial}
                onChange={(e) => setDetectedSerial(e.target.value.toUpperCase())}
                placeholder="e.g., A22B"
                maxLength={5}
                style={{
                  ...serialInputStyle,
                  border: '2px solid #dc3545',
                  boxShadow: detectedSerial ? 'none' : '0 0 0 0.1rem rgba(220, 53, 69, 0.25)',
                }}
              />
              {!detectedSerial && (
                <span style={requiredIndicatorStyle}>* Required</span>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div style={instructionsStyle}>
            <p>📝 <strong>Instructions:</strong></p>
            <ol style={instructionListStyle}>
              <li>Position the QR code intercom in the camera view</li>
              <li>Once QR is detected, manually enter the serial number above</li>
              <li>Serial length: 4 or 5 characters (e.g., A23B)</li>
              <li>Click "Continue" when both are entered</li>
            </ol>
          </div>

          {/* Action buttons */}
          <div style={buttonContainerStyle}>
            <button
              onClick={onClose}
              style={cancelButtonStyle}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!detectedQrUrl || !detectedSerial}
              style={{
                ...confirmButtonStyle,
                opacity: (!detectedQrUrl || !detectedSerial) ? 0.5 : 1,
                cursor: (!detectedQrUrl || !detectedSerial) ? 'not-allowed' : 'pointer',
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  borderBottom: '1px solid #e0e0e0',
  background: 'linear-gradient(135deg, #6600ff, #4d00c0)',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '22px',
  fontWeight: '600',
  color: 'white',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'white',
  fontSize: '24px',
  cursor: 'pointer',
  padding: '0',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: React.CSSProperties = {
  padding: '20px',
  maxHeight: 'calc(90vh - 80px)',
  overflowY: 'auto',
};

const videoContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  backgroundColor: '#000',
  borderRadius: '8px',
  overflow: 'hidden',
  marginBottom: '20px',
};

const videoStyle: React.CSSProperties = {
  width: '100%',
  height: 'auto',
  display: 'block',
};

const statusOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '10px',
  right: '10px',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  borderRadius: '8px',
  padding: '10px',
  color: 'white',
};

const statusItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '14px',
  marginBottom: '5px',
};

const statusDotStyle: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  flexShrink: 0,
};

const controlsOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '10px',
  left: '10px',
  right: '10px',
  display: 'flex',
  justifyContent: 'center',
};

const inversionButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: '20px',
  border: 'none',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: '#333',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
};

const inversionButtonActiveStyle: React.CSSProperties = {
  ...inversionButtonStyle,
  backgroundColor: '#6600ff',
  color: 'white',
};

const resultsStyle: React.CSSProperties = {
  backgroundColor: '#f5f5f5',
  borderRadius: '8px',
  padding: '15px',
  marginBottom: '20px',
};

const resultItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px',
  fontSize: '14px',
};

const resultValueStyle: React.CSSProperties = {
  flex: 1,
  fontFamily: 'monospace',
  padding: '5px 10px',
  backgroundColor: 'white',
  borderRadius: '4px',
  border: '1px solid #ddd',
  wordBreak: 'break-all',
};

const serialInputStyle: React.CSSProperties = {
  width: '140px',
  fontFamily: 'monospace',
  padding: '6px 8px',
  fontSize: '12px',
  fontWeight: '400',
  backgroundColor: 'white',
  border: '2px solid #6600ff',
  borderRadius: '6px',
  outline: 'none',
  textAlign: 'center',
  letterSpacing: '1px',
};

const instructionsStyle: React.CSSProperties = {
  backgroundColor: '#e8f5e9',
  borderRadius: '8px',
  padding: '15px',
  marginBottom: '20px',
  fontSize: '13px',
};

const instructionListStyle: React.CSSProperties = {
  margin: '10px 0 0 20px',
  lineHeight: '1.6',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '16px',
  cursor: 'pointer',
};

const confirmButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: 'pointer',
  minWidth: '180px',
};

const errorStyle: React.CSSProperties = {
  backgroundColor: '#f8d7da',
  border: '1px solid #f5c6cb',
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '20px',
  color: '#721c24',
  fontSize: '14px',
};

const requiredIndicatorStyle: React.CSSProperties = {
  color: '#dc3545',
  fontSize: '12px',
  fontWeight: '500',
  marginLeft: '8px',
};


